/**
 * Pure viewport geometry for the read-only floor map (TableFloorMap).
 *
 * The component keeps state and gestures; every coordinate decision lives
 * here so it can be unit-tested. World units are the layout builder's floor
 * units; screen units are CSS px. A world point renders at
 *   screen = (world − surface.min) · zoom + pan
 * where `pan` is the screen position of the surface's top-left corner.
 */

export type FloorBounds = { minX: number; minY: number; width: number; height: number }
export type ViewportSize = { w: number; h: number }
export type PanOffset = { x: number; y: number }

// Breathing margin (16px per side) kept between the drawn floor and the
// viewport edge when sizing the surface to the viewport / clamping the pan.
export const VIEWPORT_PAD_PX = 32
export const EDGE_PAD_PX = VIEWPORT_PAD_PX / 2

/**
 * The drawn floor surface for a given zoom: at least the tables' footprint,
 * expanded to fill the viewport at the *current* zoom with the tables
 * centered on it — so a sparse floor still reads as a floor, and the visible
 * grid always agrees with where the tables are. Sizing to the live zoom
 * (not the fit scale) is what keeps a manually-zoomed view coherent.
 */
export function surfaceForZoom(bounds: FloorBounds, viewport: ViewportSize, zoom: number): FloorBounds {
  const targetW = viewport.w ? Math.max(0, viewport.w - VIEWPORT_PAD_PX) / zoom : 0
  const targetH = viewport.h ? Math.max(0, viewport.h - VIEWPORT_PAD_PX) / zoom : 0
  const width = Math.max(bounds.width, targetW)
  const height = Math.max(bounds.height, targetH)
  return {
    minX: bounds.minX - (width - bounds.width) / 2,
    minY: bounds.minY - (height - bounds.height) / 2,
    width,
    height,
  }
}

// Per axis: center the surface while it fits the viewport; once it's larger,
// clamp so its edges can't be dragged past the viewport edge.
function clampAxis(t: number, view: number, scaled: number): number {
  return scaled <= view ? (view - scaled) / 2 : Math.min(EDGE_PAD_PX, Math.max(view - scaled - EDGE_PAD_PX, t))
}

/** Clamp a raw pan so the surface can never be dragged (or zoomed) out of reach. */
export function clampPan(
  pan: PanOffset,
  viewport: ViewportSize,
  surface: FloorBounds,
  zoom: number,
): PanOffset {
  return {
    x: clampAxis(pan.x, viewport.w, surface.width * zoom),
    y: clampAxis(pan.y, viewport.h, surface.height * zoom),
  }
}

/**
 * Pan offset that keeps the world point currently under viewport point
 * (cx, cy) fixed on screen across a zoom change — including the surface
 * re-centering that the zoom change itself causes.
 *
 * Derivation: world under the anchor is w = min + (c − t)/z; requiring
 * c = t' + (w − min')·z' gives t' = c − (c − t)·z'/z − (min − min')·z'.
 */
export function anchorPanForZoom(
  pan: PanOffset,
  zoom: number,
  nextZoom: number,
  anchor: PanOffset,
  surface: FloorBounds,
  nextSurface: FloorBounds,
): PanOffset {
  return {
    x: anchor.x - ((anchor.x - pan.x) * nextZoom) / zoom - (surface.minX - nextSurface.minX) * nextZoom,
    y: anchor.y - ((anchor.y - pan.y) * nextZoom) / zoom - (surface.minY - nextSurface.minY) * nextZoom,
  }
}
